import {
  Badge,
  Box,
  Card,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  CategoryScale,
  Chart as ChartJS,
  Title as ChartTitle,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import isBetween from "dayjs/plugin/isBetween";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Activity, ChartLine, TrendingUp, Users } from "tabler-icons-react";

// Register chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  Tooltip,
  Legend,
  Filler,
);

// Register dayjs plugins
dayjs.extend(isBetween);
dayjs.locale("fr");

export const UsageMetricCard = ({ title, value, subtitle, icon, color }) => (
  <Card padding="md" radius="md" withBorder>
    <Group position="apart" mb="xs">
      <ThemeIcon color={color} variant="light" size="lg">
        {icon}
      </ThemeIcon>
    </Group>
    <Text size="xs" color="dimmed" transform="uppercase" weight={600} mb={4}>
      {title}
    </Text>
    <Text size="xl" weight={700} mb={4}>
      {value}
    </Text>
    {subtitle && (
      <Text size="xs" color="dimmed">
        {subtitle}
      </Text>
    )}
  </Card>
);

export const useAppUsageData = (
  parentsData,
  dateRange,
  periodFilter,
  provenanceFilter = "all",
) => {
  // Data processing for App Usage Chart (based on last_credit_usage)
  const appUsageChartData = useMemo(() => {
    const { start, end } = dateRange;
    const labels = [];
    const dataMap = new Map();

    // Generate labels and initialize map based on period
    const isYearOrAll = periodFilter === "year" || periodFilter === "all";

    if (isYearOrAll) {
      // Group by Month
      let currMonth = start.clone().startOf("month");
      while (currMonth.isBefore(end) || currMonth.isSame(end, "month")) {
        const label = currMonth.format("MMM YYYY");
        labels.push(label);
        dataMap.set(label, 0);
        currMonth = currMonth.add(1, "month");
      }
    } else {
      // Group by Day
      let current = start.clone();
      while (current.isBefore(end) || current.isSame(end, "day")) {
        const label = current.format("DD MMM");
        labels.push(label);
        dataMap.set(label, 0);
        current = current.add(1, "day");
      }
    }

    // Process parents with last_credit_usage
    let totalUsage = 0;
    let activeUsers = 0;
    const uniqueUsers = new Set();
    let maxUsageInDay = 0;

    if (parentsData?.data) {
      parentsData.data.forEach((parent) => {
        // Filtrer par provenance (comparaison insensible à la casse)
        if (
          provenanceFilter !== "all" &&
          (!parent.provenance ||
            parent.provenance.toLowerCase() !== provenanceFilter.toLowerCase())
        ) {
          return;
        }
        const usageDate = parent.last_credit_usage;
        if (!usageDate) return;

        const date = dayjs(usageDate);
        if (!date.isValid()) return;

        if (date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")) {
          const label = isYearOrAll
            ? date.format("MMM YYYY")
            : date.format("DD MMM");

          if (dataMap.has(label)) {
            dataMap.set(label, dataMap.get(label) + 1);
            maxUsageInDay = Math.max(maxUsageInDay, dataMap.get(label));
          }
          totalUsage++;
          uniqueUsers.add(parent.id || parent._id);
        }
      });
      activeUsers = uniqueUsers.size;
    }

    // Calculer la moyenne d'utilisation par jour
    const avgUsagePerDay =
      labels.length > 0 ? (totalUsage / labels.length).toFixed(1) : 0;

    return {
      labels,
      chartValues: labels.map((l) => dataMap.get(l)),
      totalUsage,
      activeUsers,
      avgUsagePerDay,
      maxUsageInDay,
    };
  }, [dateRange, parentsData, periodFilter, provenanceFilter]);

  return appUsageChartData;
};

const AppUsageChart = ({
  parentsData,
  isLoading,
  dateRange,
  periodFilter,
  showCards = true,
  provenanceFilter = "all",
}) => {
  const appUsageChartData = useAppUsageData(
    parentsData,
    dateRange,
    periodFilter,
    provenanceFilter,
  );

  // Chart data for App Usage
  const appUsageChartConfig = {
    labels: appUsageChartData.labels,
    datasets: [
      {
        fill: true,
        label: "Utilisations",
        data: appUsageChartData.chartValues,
        borderColor: "#3b82f6",
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(59, 130, 246, 0.5)");
          gradient.addColorStop(1, "rgba(59, 130, 246, 0.0)");
          return gradient;
        },
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#3b82f6",
        borderWidth: 2,
      },
    ],
  };

  // Options for App Usage Chart
  const appUsageOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: { size: 13 },
        bodyFont: { size: 13 },
        displayColors: false,
        callbacks: {
          label: (context) => `${context.parsed.y} Utilisations`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: { maxTicksLimit: 10, color: "#9ca3af" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f3f4f6", borderDash: [5, 5], drawBorder: false },
        ticks: { stepSize: 1, color: "#9ca3af" },
      },
    },
    maintainAspectRatio: false,
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
  };

  return (
    <Stack spacing="md">
      {/* Cartes de métriques */}
      {showCards && (
        <SimpleGrid
          cols={4}
          spacing="md"
          breakpoints={[
            { maxWidth: "md", cols: 2 },
            { maxWidth: "sm", cols: 1 },
          ]}
        >
          <UsageMetricCard
            title="Total utilisations"
            value={appUsageChartData.totalUsage}
            subtitle="Sur la période"
            icon={<Activity size={20} />}
            color="blue"
          />
          <UsageMetricCard
            title="Parents actifs"
            value={appUsageChartData.activeUsers}
            subtitle="Parents ayant utilisé des crédits"
            icon={<Users size={20} />}
            color="green"
          />
          <UsageMetricCard
            title="Moyenne / jour"
            value={appUsageChartData.avgUsagePerDay}
            subtitle="Utilisations par jour"
            icon={<ChartLine size={20} />}
            color="cyan"
          />
          <UsageMetricCard
            title="Pic d'utilisation"
            value={appUsageChartData.maxUsageInDay}
            subtitle="Maximum en un jour"
            icon={<TrendingUp size={20} />}
            color="orange"
          />
        </SimpleGrid>
      )}

      {/* Graphique */}
      <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
        <Group position="apart" mb="md">
          <Text size="lg" weight={700}>
            Utilisation quotidienne
          </Text>
          <Badge variant="light" color="blue" size="lg">
            {appUsageChartData.totalUsage} utilisations
          </Badge>
        </Group>

        <div style={{ height: "300px" }}>
          {isLoading ? (
            <Box
              h="100%"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Loader size="lg" />
            </Box>
          ) : (
            <Line options={appUsageOptions} data={appUsageChartConfig} />
          )}
        </div>

        <Text
          align="center"
          color="dimmed"
          size="xs"
          mt="md"
          transform="uppercase"
          weight={600}
          letterSpacing={0.5}
        >
          Utilisation des crédits par période
        </Text>
      </Card>
    </Stack>
  );
};

export default AppUsageChart;
